import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import NavigationHeader from "../../components/ui/NavigationHeader";
import MainNavigation from "../../components/ui/MainNavigation";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import CategoryTree from "./components/CategoryTree";
import CategoryDetails from "./components/CategoryDetails";
import CategoryProducts from "./components/CategoryProducts";
import BulkActionsModal from "./components/BulkActionsModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createCategorieservice,
  deleteCategorieservice,
  getAllCategoriesService,
} from "redux/services/categories_service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const CategoryManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { categoryList, metaCategory } = useSelector(
    (state) => state.categories
  );

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Mock user data
  const user = {
    name: t("userName"),
    email: t("userEmail"),
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };

  // State to hold categories
  const [categories, setCategories] = useState([]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsEditing(false);
    setActiveTab("details");
  };

  const handleCategoryUpdate = (updatedCategory) => {
    const updateCategoryInTree = (categories) => {
      return categories.map((category) => {
        if (category.id === updatedCategory.id) {
          return { ...category, ...updatedCategory };
        }
        if (category.children) {
          return {
            ...category,
            children: updateCategoryInTree(category.children),
          };
        }
        return category;
      });
    };

    setCategories(updateCategoryInTree(categories));
    setSelectedCategory(updatedCategory);
  };

  const handleCategoryCreate = (newCategory) => {
    const formData = new FormData();

    formData.append(
      "translations[en][name]",
      newCategory?.translations?.en?.name
    );
    formData.append(
      "translations[ar][name]",
      newCategory?.translations?.ar?.name
    );
    formData.append("image", newCategory?.image);
    formData.append("order", newCategory?.order);
    dispatch(createCategorieservice(formData)).then((response) => {
      if (
        response?.payload?.status == 200 ||
        response?.payload?.status == 201
      ) {
        toast(t("addedSuccess"));
        dispatch(getAllCategoriesService({ page: 1 }));
      }
    });
  };

  const handleCategoryDelete = (categoryId) => {
    const findCategory = (categories, id) => {
      for (const category of categories) {
        if (category.id === id) return category;
        if (category.children) {
          const found = findCategory(category.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const categoryToDelete = findCategory(categories, categoryId);
    setCategoryToDelete(categoryToDelete);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (categoryId) => {
    await dispatch(deleteCategorieservice(categoryId)).then((response) => {
      if (response?.payload?.status == 200) {
        toast(t("deletedSuccess"));
        dispatch(getAllCategoriesService({ page: 1 }));
        setShowDeleteModal(false);
        setCategoryToDelete(null);
      }
    });
  };

  const handleBulkAction = (actionData) => {
    if (actionData.action === "delete") {
      selectedCategories.forEach((categoryId) => {
        handleDeleteConfirm(categoryId);
      });
    }
    setSelectedCategories([]);
    setShowBulkModal(false);
  };

  const fetchCategories = (page) => {
    dispatch(getAllCategoriesService({ page })).then((response) => {
      if (response.payload) {
        setCategories(response.payload.data.data);
        setTotalPages(response.payload?.data?.meta.meta.last_page);
      }
    });
  };

  useEffect(() => {
    fetchCategories(currentPage);
  }, [dispatch, currentPage]);

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>{t("title")}</title>
        <meta name="description" content={t("description")} />
      </Helmet>

      {/* Navigation Header 
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

   
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />*/}

      {/* Main Content */}

      {/* Breadcrumb Navigation 
        <BreadcrumbNavigation />*/}

      {/* Page Header */}
      <div className="border-border">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
           
              <div>
                <h1 className="text-2xl font-semibold text-foreground">
                  {t("Discover")}
                </h1>
           
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {selectedCategories.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setShowBulkModal(true)}
                  iconName="Settings"
                  iconPosition="left"
                >
                  {t("bulkActions")} ({selectedCategories.length})
                </Button>
              )}

              <Button
                onClick={() => {
                  setSelectedCategory(null);
                  setIsEditing(true);
                }}
                iconName="Plus"
                iconPosition="left"
              >
                {t("addCategory")}
              </Button>
            </div>
          </div>

          {/* Search and Filters 
          <div className="flex items-center space-x-4">
            <div className="flex-1 max-w-md">
              <Input
                type="search"
                placeholder={t("searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="BarChart3" size={16} />
              <span>
                {metaCategory?.total} {t("categories")}
              </span>
              <span>•</span>
              <span>
                {categories.reduce((sum, cat) => sum + cat.productCount, 0)}{" "}
                {t("products")}
              </span>
            </div>
          </div>*/}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6">
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-280px)]">
          {/* Left Panel - Category Tree */}
          <div className="col-span-12 lg:col-span-5 bg-white rounded-lg border border-border shadow-elevation-1">
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-border">
                <h3 className="font-medium text-foreground flex items-center space-x-2">
                  <Icon name="TreePine" size={16} />
                  <span>{t("categoryHierarchy")}</span>
                </h3>
              </div>

              <div className="flex-1 overflow-hidden">
                <CategoryTree
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategorySelect={handleCategorySelect}
                  onCategoryDelete={handleCategoryDelete}
                  searchTerm={searchTerm}
                />
              </div>
              {/* Pagination Controls */}
              <div className="flex justify-between p-4">
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  {t("previous")}
                </Button>
                <span>
                  Page {currentPage} {t("of")} {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  {t("next")}
                </Button>
              </div>
            </div>
          </div>

          {/* Right Panel - Category Details */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            {/* Tab Navigation */}
            {selectedCategory && (
              <div className="bg-white rounded-lg border border-border shadow-elevation-1">
                <div className="border-b border-border">
                  <nav className="flex space-x-8 px-6">
                    <button
                      onClick={() => setActiveTab("details")}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-micro ${
                        activeTab === "details"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="Info" size={16} />
                        <span>{t("details")}</span>
                      </div>
                    </button>

                    <button
                      onClick={() => setActiveTab("products")}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-micro ${
                        activeTab === "products"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="Package" size={16} />
                        <span>
                          {t("categoryProducts")} ({categoryProducts.length})
                        </span>
                      </div>
                    </button>
                  </nav>
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="bg-white rounded-lg border border-border shadow-elevation-1 h-full">
              {activeTab === "details" ? (
                <CategoryDetails
                  category={selectedCategory}
                  categories={categories}
                  onCategoryUpdate={handleCategoryUpdate}
                  onCategoryCreate={handleCategoryCreate}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                />
              ) : (
                <CategoryProducts category={selectedCategory} products={[]} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BulkActionsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedCategories={selectedCategories}
        categories={categories}
        onBulkAction={handleBulkAction}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        category={categoryToDelete}
        categories={categories}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default CategoryManagement;
